import { Badge } from '@wedding-tech/ui/badge'
import { Input } from '@wedding-tech/ui/input'
import {
  CheckCircle2,
  ChevronRight,
  Loader2,
  Search,
  UserPlus,
  X,
} from 'lucide-react'
import { useMemo, useState } from 'react'

import { cn } from '@/lib/utils'

import { CheckinLayout } from './CheckinLayout'
import type { GuestItem } from './types'

interface SearchStepProps {
  searchQuery: string
  onSearchQueryChange: (val: string) => void
  guests: GuestItem[]
  isFetching: boolean
  onSelectGuest: (guest: GuestItem) => void
  onManualRegister: () => void
}

export function SearchStep({
  searchQuery,
  onSearchQueryChange,
  guests,
  isFetching,
  onSelectGuest,
  onManualRegister,
}: SearchStepProps) {
  const [selectedGroup, setSelectedGroup] = useState<string>('all')

  // Extract distinct groups for group filtering
  const groups = useMemo(() => {
    const set = new Set<string>()
    guests.forEach((g) => {
      if (g.group) set.add(g.group)
    })
    return Array.from(set).sort()
  }, [guests])

  // Filter guests by search query and group
  const filteredGuests = useMemo(() => {
    let list = guests

    if (selectedGroup !== 'all') {
      list = list.filter((g) => g.group === selectedGroup)
    }

    if (!searchQuery.trim()) {
      return list
    }

    const q = searchQuery.toLowerCase().trim()
    return list.filter(
      (g) =>
        g.fullName.toLowerCase().includes(q) ||
        (g.displayName ?? '').toLowerCase().includes(q) ||
        (g.group ?? '').toLowerCase().includes(q),
    )
  }, [guests, searchQuery, selectedGroup])

  return (
    <CheckinLayout
      title="เช็กอินเข้าร่วมงาน"
      subtitle="ค้นหาชื่อของคุณในรายชื่อแขกเพื่อทำการเช็กอิน"
    >
      {/* Search Input Bar */}
      <div className="relative">
        <Search className="text-muted-foreground absolute top-1/2 left-3.5 size-4.5 -translate-y-1/2" />
        <Input
          type="text"
          aria-label="ค้นหาชื่อหรือกลุ่ม"
          placeholder="พิมพ์ชื่อ นามสกุล หรือชื่อเล่น..."
          value={searchQuery}
          onChange={(e) => onSearchQueryChange(e.target.value)}
          className="h-12 pr-10 pl-10 text-base"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => onSearchQueryChange('')}
            className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3.5 -translate-y-1/2 rounded-full p-1 transition-colors"
            aria-label="ล้างการค้นหา"
          >
            <X className="size-4" />
          </button>
        )}
      </div>

      {/* Group Filter Chips (if any groups exist) */}
      {groups.length > 0 && (
        <div className="flex scrollbar-none items-center gap-1.5 overflow-x-auto pb-0.5">
          <button
            type="button"
            onClick={() => setSelectedGroup('all')}
            className={cn(
              'shrink-0 rounded-xl px-3.5 py-1.5 text-xs font-semibold transition-all',
              selectedGroup === 'all'
                ? 'bg-primary text-primary-foreground shadow-xs'
                : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80',
            )}
          >
            ทั้งหมด
          </button>
          {groups.map((grp) => (
            <button
              key={grp}
              type="button"
              onClick={() => setSelectedGroup(grp)}
              className={cn(
                'shrink-0 rounded-xl px-3.5 py-1.5 text-xs font-semibold transition-all',
                selectedGroup === grp
                  ? 'bg-primary text-primary-foreground shadow-xs'
                  : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80',
              )}
            >
              {grp}
            </button>
          ))}
        </div>
      )}

      {/* Guest Results */}
      <div className="space-y-2.5">
        {isFetching ? (
          <div className="text-muted-foreground flex items-center justify-center gap-2 py-12 text-sm font-medium">
            <Loader2 className="text-primary size-5 animate-spin" />
            <span>กำลังโหลดรายชื่อ...</span>
          </div>
        ) : filteredGuests.length === 0 ? (
          <div className="bg-card rounded-2xl border p-8 text-center shadow-xs">
            <p className="text-foreground text-sm font-semibold">
              ไม่พบรายชื่อที่ค้นหา
            </p>
            <p className="text-muted-foreground mt-1 text-xs">
              ลองตรวจสอบการสะกดคำ หรือลงทะเบียนด้วยตนเองด้านล่าง
            </p>
          </div>
        ) : (
          filteredGuests.map((guest) => {
            const count = guest.checkedInCount || 0
            const capacity = guest.expectedFollowers || 1
            const isFullyCheckedIn = count >= capacity
            const isPartiallyCheckedIn = count > 0 && !isFullyCheckedIn

            return (
              <button
                key={guest.id}
                type="button"
                onClick={() => onSelectGuest(guest)}
                className={cn(
                  'group bg-card flex w-full items-center justify-between rounded-2xl border p-4 text-left shadow-xs transition-all active:scale-[0.99]',
                  isFullyCheckedIn
                    ? 'border-green-200/80 bg-green-50/40 hover:bg-green-50/70'
                    : isPartiallyCheckedIn
                      ? 'border-amber-200/80 bg-amber-50/40 hover:bg-amber-50/70'
                      : 'hover:border-primary/50 hover:bg-accent/40',
                )}
              >
                <div className="min-w-0 flex-1 pr-3">
                  <p className="text-foreground truncate text-base leading-snug font-bold">
                    {guest.fullName}
                  </p>
                  {guest.displayName &&
                    guest.displayName !== guest.fullName && (
                      <p className="text-muted-foreground truncate text-xs font-medium">
                        ({guest.displayName})
                      </p>
                    )}
                  {guest.group && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      <Badge
                        variant="outline"
                        className="px-2 py-0 text-[11px] font-normal"
                      >
                        {guest.group}
                      </Badge>
                    </div>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-2 text-right">
                  {isFullyCheckedIn ? (
                    <Badge
                      variant="default"
                      className="bg-green-600 text-xs font-semibold hover:bg-green-700"
                    >
                      <CheckCircle2 className="mr-1 size-3" />
                      เช็กอินแล้ว ({count}/{capacity})
                    </Badge>
                  ) : isPartiallyCheckedIn ? (
                    <Badge
                      variant="outline"
                      className="border-amber-400 bg-amber-50 text-xs font-semibold text-amber-800"
                    >
                      เช็กอินแล้ว {count}/{capacity}
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="text-muted-foreground text-xs"
                    >
                      {capacity > 1 ? `${capacity} ท่าน` : '1 ท่าน'}
                    </Badge>
                  )}
                  <ChevronRight className="text-muted-foreground group-hover:text-foreground size-4 transition-transform group-hover:translate-x-0.5" />
                </div>
              </button>
            )
          })
        )}
      </div>

      {/* Manual / Walk-in Registration Fallback */}
      <div className="border-border/60 border-t pt-6">
        <p className="text-muted-foreground mb-3 text-center text-xs font-medium">
          ไม่พบชื่อของคุณในรายชื่อแขก?
        </p>
        <button
          type="button"
          onClick={onManualRegister}
          className="bg-card hover:bg-accent/50 border-border/80 flex w-full items-center gap-3.5 rounded-2xl border-2 border-dashed p-4 text-left shadow-xs transition-all active:scale-[0.99]"
        >
          <div className="bg-primary/10 text-primary flex size-11 shrink-0 items-center justify-center rounded-xl">
            <UserPlus className="size-5.5" />
          </div>
          <div>
            <p className="text-foreground text-sm font-bold">
              ลงทะเบียนด้วยตนเอง (Walk-in)
            </p>
            <p className="text-muted-foreground text-xs">
              กรอกข้อมูลเพื่อเช็กอินเข้าร่วมงานทันที
            </p>
          </div>
        </button>
      </div>
    </CheckinLayout>
  )
}
